import CartAttributes from 'cart-attributes';

class DeliveryWidget extends HTMLElement {
  static sunday = 0;
  static saturday = 6;

  static metaobjectWeekdayNames = DeliveryWidget.getWeekdayNames('de');

  static addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  }

  static getWeekdayNames(locale) {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'long' });
    const sunday = new Date(2024, 0, 7);

    return Array.from({ length: 7 }, (_, weekday) => formatter.format(DeliveryWidget.addDays(sunday, weekday)));
  }

  static isWeekend(weekday) {
    return weekday === DeliveryWidget.saturday || weekday === DeliveryWidget.sunday;
  }

  static toIsoDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
  }

  static isWorkingDay(date, vacationDays) {
    const isVacationDay = vacationDays.has(DeliveryWidget.toIsoDate(date));

    return !DeliveryWidget.isWeekend(date.getDay()) && !isVacationDay;
  }

  static getOrderDate(vacationDays) {
    let orderDate = new Date();

    while (!DeliveryWidget.isWorkingDay(orderDate, vacationDays)) {
      orderDate = DeliveryWidget.addDays(orderDate, 1);
    }

    return orderDate;
  }

  static saveCartDeliveryDate(isoDate) {
    return CartAttributes.update({
      _b2b_local: isoDate ? 'true' : '',
      Delivery: isoDate,
    });
  }

  static setButtonLabel(button, label) {
    const buttonContent = button.querySelector('button-content') ?? button;

    buttonContent.textContent = label;
    ['rotated-text', 'up-text']
      .filter((attribute) => buttonContent.hasAttribute(attribute))
      .forEach((attribute) => buttonContent.setAttribute(attribute, label));
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  get deliveryDays() {
    return JSON.parse(this.dataset.deliveryDays) ?? [];
  }

  get vacationDays() {
    return JSON.parse(this.dataset.vacationDays) ?? [];
  }

  get offsetDays() {
    return JSON.parse(this.dataset.offsetDays) ?? 0;
  }

  get datesToShow() {
    return Number(this.dataset.datesToShow);
  }

  get locale() {
    return this.dataset.locale;
  }

  get summaryTemplate() {
    return this.dataset.summaryTemplate;
  }

  get savedDate() {
    return this.dataset.savedDate;
  }

  get missingDateHint() {
    return this.dataset.missingDateHint;
  }

  get expiredDateHint() {
    return this.dataset.expiredDateHint;
  }

  get checkoutLabel() {
    return this.dataset.checkoutLabel;
  }

  get selectDateLabel() {
    return this.dataset.selectDateLabel;
  }

  get missingBottlesLabel() {
    return this.dataset.missingBottlesLabel;
  }

  get checkoutButtonLabel() {
    if (this.isDisabled) return this.missingBottlesLabel;
    if (this.findCalendarDay(this.savedDate)) return this.checkoutLabel;

    return this.selectDateLabel;
  }

  get deliveryWeekdays() {
    const weekdays = this.deliveryDays
      .map(({ day }) => DeliveryWidget.metaobjectWeekdayNames.indexOf(day))
      .filter((weekday) => weekday !== -1);

    return new Set(weekdays);
  }

  get fieldset() {
    return this.querySelector('.delivery-widget__fieldset');
  }

  get isDisabled() {
    return this.fieldset.disabled;
  }

  get calendar() {
    return this.querySelector('.delivery-widget__delivery-calendar');
  }

  get calendarDayTemplate() {
    return this.querySelector('[data-delivery-calendar-template]');
  }

  get calendarDays() {
    return [...this.calendar.querySelectorAll('.delivery-widget__delivery-calendar-day')];
  }

  get selectedCalendarDay() {
    return this.calendar.querySelector('[aria-pressed="true"]');
  }

  get hasDeliveryDates() {
    return this.calendarDays.length > 0;
  }

  get isSavedDateAvailable() {
    const { savedDate } = this;
    return this.getDeliveryDates().some((date) => DeliveryWidget.toIsoDate(date) === savedDate);
  }

  get summary() {
    return this.querySelector('.delivery-widget__summary');
  }

  init() {
    this.abortController = new AbortController();
    this.pendingSaves = 0;
    this.calendar.addEventListener('click', (event) => this.handleCalendarClick(event), {
      signal: this.abortController.signal,
    });
    document.addEventListener('submit', (event) => this.handleCheckoutSubmit(event), {
      signal: this.abortController.signal,
    });

    this.render();
    this.hidden = !this.hasDeliveryDates;
    this.restoreSavedDate();
  }

  cleanup() {
    this.abortController.abort();
    this.calendar.replaceChildren(this.calendarDayTemplate);
    this.summary.textContent = '';
  }

  render() {
    const calendarDays = this.getDeliveryDates().map((date) => this.createCalendarDay(date));
    this.calendar.replaceChildren(this.calendarDayTemplate, ...calendarDays);
  }

  restoreSavedDate() {
    if (this.isDisabled && this.savedDate) {
      this.dataset.savedDate = '';
      DeliveryWidget.saveCartDeliveryDate('');
    }

    const savedCalendarDay = this.findCalendarDay(this.savedDate);

    this.hasExpiredDate = Boolean(this.savedDate) && !savedCalendarDay;
    this.showSelection(savedCalendarDay);
    this.updateCheckoutButtons();
  }

  handleCalendarClick(event) {
    const calendarDay = event.target.closest('.delivery-widget__delivery-calendar-day');
    if (calendarDay?.matches(':enabled')) this.selectCalendarDay(calendarDay);
  }

  handleCheckoutSubmit(event) {
    if (event.submitter?.name !== 'checkout' || !this.hasDeliveryDates) return;
    if (this.pendingSaves === 0 && this.isSavedDateAvailable) return;

    event.preventDefault();

    if (this.pendingSaves > 0) return;

    if (this.savedDate) {
      this.hasExpiredDate = true;
      this.render();
      this.showSelection(null);
      this.saveDeliveryDate('');
    }

    this.scrollToCalendar();
  }

  scrollToCalendar() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    this.calendarDays[0].focus({ preventScroll: true });
  }

  async selectCalendarDay(calendarDay) {
    if (calendarDay === this.selectedCalendarDay) return;

    this.showSelection(calendarDay);

    const isSaved = await this.saveDeliveryDate(calendarDay.value);

    if (isSaved) {
      this.hasExpiredDate = false;
    } else if (calendarDay === this.selectedCalendarDay) {
      this.showSelection(this.findCalendarDay(this.savedDate));
    }
  }

  async saveDeliveryDate(isoDate) {
    this.pendingSaves += 1;
    this.updateCheckoutButtons();
    const isSaved = await DeliveryWidget.saveCartDeliveryDate(isoDate);
    this.pendingSaves -= 1;

    if (isSaved) this.dataset.savedDate = isoDate;

    this.updateCheckoutButtons();

    return isSaved;
  }

  updateCheckoutButtons() {
    if (!this.isConnected || !this.hasDeliveryDates) return;

    const isSaving = this.pendingSaves > 0;
    const isBlocked = isSaving || this.isDisabled;
    const label = this.checkoutButtonLabel;

    document.querySelectorAll('button[name="checkout"]').forEach((button) => {
      button.disabled = this.isDisabled;
      button.setAttribute('aria-disabled', String(isBlocked));
      button.setAttribute('aria-busy', String(isSaving));
      DeliveryWidget.setButtonLabel(button, label);
    });
  }

  findCalendarDay(isoDate) {
    return this.calendarDays.find(({ value }) => value === isoDate);
  }

  showSelection(selectedCalendarDay) {
    this.calendarDays.forEach((calendarDay) => {
      calendarDay.setAttribute('aria-pressed', String(calendarDay === selectedCalendarDay));
    });

    if (selectedCalendarDay) {
      this.showSummary(selectedCalendarDay.dataset.summary);
    } else if (this.hasExpiredDate) {
      this.showSummary(this.expiredDateHint, { isError: true });
    } else {
      this.showSummary(this.missingDateHint);
    }
  }

  showSummary(text, { isError = false } = {}) {
    this.summary.textContent = text;
    this.summary.classList.toggle('delivery-widget__summary--error', isError);
  }

  getDeliveryDates() {
    const { deliveryWeekdays, datesToShow } = this;
    const vacationDays = new Set(this.vacationDays);
    const deliveryDates = [];
    if (deliveryWeekdays.size === 0) return deliveryDates;

    let date = DeliveryWidget.addDays(DeliveryWidget.getOrderDate(vacationDays), this.offsetDays);

    while (deliveryDates.length < datesToShow) {
      const isVacationDay = vacationDays.has(DeliveryWidget.toIsoDate(date));

      if (deliveryWeekdays.has(date.getDay()) && !isVacationDay) {
        deliveryDates.push(date);
      }

      date = DeliveryWidget.addDays(date, 1);
    }

    return deliveryDates;
  }

  createCalendarDay(date) {
    const calendarDay = this.calendarDayTemplate.content.firstElementChild.cloneNode(true);
    const weekdayLabel = calendarDay.querySelector('.delivery-widget__delivery-calendar-weekday');
    const dayLabel = calendarDay.querySelector('.delivery-widget__delivery-calendar-day-date');
    const monthLabel = calendarDay.querySelector('.delivery-widget__delivery-calendar-month');

    calendarDay.value = DeliveryWidget.toIsoDate(date);
    calendarDay.dataset.summary = this.getSummaryText(date);
    weekdayLabel.textContent = this.formatDate(date, { weekday: 'short' });
    dayLabel.textContent = this.formatDate(date, { day: 'numeric' });
    monthLabel.textContent = this.formatDate(date, { month: 'short' });

    return calendarDay;
  }

  getSummaryText(date) {
    const formattedDate = this.formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' });

    return this.summaryTemplate
      .replace('[date]', formattedDate)
      .replace('[time_frame]', this.getTimeFrame(date))
      .trim();
  }

  getTimeFrame(date) {
    const weekdayName = DeliveryWidget.metaobjectWeekdayNames[date.getDay()];
    const deliveryDay = this.deliveryDays.find(({ day }) => day === weekdayName);

    return deliveryDay.delivery_time_frame ?? '';
  }

  formatDate(date, options) {
    return new Intl.DateTimeFormat(this.locale, options).format(date);
  }
}

if (!customElements.get('delivery-widget')) {
  customElements.define('delivery-widget', DeliveryWidget);
}
